import React from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'

import Avatar from 'components/Avatar'
import CollapsibleText from 'components/CollapsibleText'
import Icon from 'components/Icon'
import TagWithHover from 'components/TagWithHover'
import Typography from 'components/Typography'
import UserName from 'components/UserName'
import { EventContext, useEventContext } from 'context/EventContext'
import { formatAvatarInitials } from 'helpers/formatters'

import classes from './ReviewsSnippet.module.scss'

const Tag = ({ snippet }) => {
  const getTagVariant = () => {
    if (dayjs().diff(dayjs(snippet?.created), 'month') <= 12) {
      return 'Highly relevant'
    }
    if (
      (snippet?.frequency_of_use === 'daily' && snippet?.time_used !== 'Less than 6 months') ||
      ((snippet?.time_used === '1-2 years' || snippet?.time_used === '2+ years') &&
        snippet?.frequency_of_use === 'weekly')
    ) {
      return 'Frequent user'
    }
    return null
  }
  const tagVariant = getTagVariant()

  return <div className={classes.tagsContainer}>{tagVariant && <TagWithHover variant={tagVariant} size="tiny" />}</div>
}

/** @type {import("./types").default} */
const ReviewsSnippet = ({ snippet = {}, snippetSize = 'small', type = 'pros' }) => {
  const isPros = type === 'pros'
  const isLarge = snippetSize === 'large'
  const fullName = snippet.user_name || [snippet.first_name, snippet.last_name].filter(Boolean).join(' ')
  const user_name = fullName || ''
  const quotedText = snippet.text.match(/“(.*?)”/) ? snippet.text : `“${snippet.text}”`
  const { sendEvent } = useEventContext({
    engagementComponent: 'product_customer_support',
    name: 'engagement_product_customer_support',
  })

  const AvatarContent = () => {
    return (
      <Avatar src={snippet.avatar} className={classes.userAvatarExp}>
        {formatAvatarInitials(user_name)}
      </Avatar>
    )
  }

  const UserDetail = ({ job }) => {
    return (
      <Typography variant="tight-xxs" className={classes.anonymity}>
        {!snippet.is_anonymous ? job : 'Anonymity request'}
      </Typography>
    )
  }

  return (
    <div className={clsx('ReviewsSnippet', classes.reviewSnippetsGeneral)}>
      <div className={classes.iconContainerExp}>
        {type && (
          <Icon
            size={16}
            className={clsx(classes[isPros ? 'iconPros' : 'iconCons'], classes.icon)}
            icon={type === 'pros' ? 'caret-circle-up' : 'caret-circle-down'}
          />
        )}

        <EventContext
          value={{
            engagementComponent: `product_reviews_${type}`,
          }}
        >
          <Tag snippet={snippet} />
        </EventContext>
      </div>
      <div
        className={clsx(classes.snippetContainer, {
          [classes.snippetContainerPadding]: isLarge,
        })}
      >
        <Typography component="div" variant="body-xs" className={classes.snippetText}>
          <CollapsibleText
            text={quotedText}
            textVariant="body-xs"
            maxNumberChars={250}
            onButtonClick={(_, { isExpanded }) => {
              sendEvent({
                engagementDestination: isExpanded ? 'expand-text' : 'collapse-text',
                engagementDetails: isExpanded ? 'text-link_read-more' : 'text-link_read-less',
                name: 'engagement_product_click',
              })
            }}
          />
        </Typography>
        <div className={clsx(classes.footerExp, classes.footer)}>
          <div className={classes.reviewerName}>
            <AvatarContent />
            <EventContext
              value={{
                engagementComponent: `product_reviews_${type}`,
              }}
            >
              <div>
                <UserName
                  isSnippet
                  userName={user_name}
                  isVerified={
                    snippet.is_validated_business_email ||
                    snippet.is_validated_proof_of_individual ||
                    snippet.is_validated
                  }
                  userInfo={snippet}
                />
                <UserDetail job={snippet?.job_title} />
              </div>
            </EventContext>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReviewsSnippet
